/**
 * Match a single character present in the list below `[а-яА-Я\-_]`
 * * `а-я` matches a single character in the range between `а` (index 1072) and `я` (index 1103) (case sensitive)
 * * `А-Я` matches a single character in the range between `А` (index 1040) and `Я` (index 1071) (case sensitive)
 *
 * `+` matches the previous token between `one` and `unlimited` times, as many times as possible, giving back as needed (greedy)
 */
export const hasCyrillicCharacters = /[а-яА-Я]+/gm;

/**
 * String contains text found inside header tags
 *
 * Match any amount of `h` tags before capturing group
 * * `+` matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
 * * `<h` matches the characters `<h` literally (case insensitive)
 * * `<` matches the character `<` with index 6010 (3C16 or 748) literally (case insensitive)
 * * `h` matches the character h with index 10410 (6816 or 1508) literally (case insensitive)
 * * `1-6` matches a single character in the range between `1` (index 49) and `6` (index 54) (case insensitive)
 * * `>` matches the character `>` with index 6210 (3E16 or 768) literally (case insensitive)
 *
 * Match any amount of any characters
 * * `.` matches any character (except for line terminators)
 * * `*?` matches the previous token between zero and unlimited times, as few times as possible, expanding as needed (lazy)
 *
 * Match a closing `h` tag
 * * `<` matches the character `<` with index 6010 (3C16 or 748) literally (case insensitive)
 * * `\/` matches the character / with index 4710 (2F16 or 578) literally (case insensitive)
 * * `h` matches the character `h` with index 10410 (6816 or 1508) literally (case insensitive)
 * * `1-6` matches a single character in the range between `1` (index 49) and `6` (index 54) (case insensitive)
 * * `>` matches the character `>` with index 6210 (3E16 or 768) literally (case insensitive)
 */
export const hasHeaderTag = /(?:<h[1-6]>)+(.*?)<\/h[1-6]>/gim;

/**
 * `<` matches the character `<` with index 6010 (3C16 or 748) literally (case sensitive)
 * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case sensitive)
 * `?` matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
 * Match a single character not present in the list below `[^>]`
 * `+` matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
 * `>` matches the character `>` with index 6210 (3E16 or 768) literally (case sensitive)
 * 1st Capturing Group `(>|$)`
 * * 1st Alternative `>`
 * * `>` matches the character `>` with index 6210 (3E16 or 768) literally (case sensitive)
 * * 2nd Alternative `$`
 * * `$` asserts position at the end of the string, or before the line terminator right at the end of the string (if any)
 */
export const hasHTMLTag = /<\/?[^>]+(>|$)/g;

/** `\s` matches any whitespace character (equivalent to `[\r\n\t\f\v \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]`) */
export const hasWhiteSpace = /\s/g;

/**
 * `^` asserts position at start of a line
 *
 * `http` matches the characters `http` literally (case insensitive)
 *
 * `s` matches the character `s` with index 11510 (7316 or 1638) literally (case insensitive)
 *
 * `?` matches the previous token between `zero` and `one` times, as many times as possible, giving back as needed (greedy)
 *
 * `:` matches the character `:` with index 5810 (3A16 or 728) literally (case insensitive)
 *
 * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 *
 * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 *
 * 1st Capturing Group `(.*)`
 * * `.` matches any character (except for line terminators)
 * * `*` matches the previous token between `zero` and `unlimited` times, as many times as possible, giving back as needed (greedy)
 */
export const isAbsoluteURL = /^https?:\/\/(.*)/gi;

/**
 * `^` asserts position at start of the string
 * Match a single character present in the list below `[A-Z0-9._%+\-]`
 * `+` matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
 * `A-Z` matches a single character in the range between `A` (index 65) and `Z` (index 90) (case insensitive)
 * `0-9` matches a single character in the range between `0` (index 48) and `9` (index 57) (case insensitive)
 * `._%+` matches a single character in the list `._%+` (case insensitive)
 * `\-` matches the character `-` with index 4510 (2D16 or 558) literally (case insensitive)
 * `@` matches the character `@` with index 6410 (4016 or 1008) literally (case insensitive)
 * Match a single character present in the list below `[A-Z0-9.\-]`
 * `+` matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy)
 * `A-Z` matches a single character in the range between `A` (index 65) and `Z` (index 90) (case insensitive)
 * `0-9` matches a single character in the range between `0` (index 48) and `9` (index 57) (case insensitive)
 * `.` matches the character `.` with index 4610 (2E16 or 568) literally (case insensitive)
 * `\-` matches the character `-` with index 4510 (2D16 or 558) literally (case insensitive)
 * `\.` matches the character `.` with index 4610 (2E16 or 568) literally (case insensitive)
 * Match a single character present in the list below `[A-Z]`
 * `{2,4}` matches the previous token between `2` and `4` times, as many times as possible, giving back as needed (greedy)
 * `A-Z` matches a single character in the range between `A` (index 65) and `Z` (index 90) (case insensitive)
 * `$` asserts position at the end of the string
 */
export const isEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i;

/**
 * 1st Alternative `(\/pricing\/lucid(chart|scale|spark))`
 * * 1st Capturing Group `(\/pricing\/lucid(chart|scale|spark))`
 *   * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 *   * `pricing` matches the characters `pricing` literally (case insensitive)
 *   * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 *   * `lucid` matches the characters `lucid` literally (case insensitive)
 * * 2nd Capturing Group `(chart|scale|spark)`
 *   * 1st Alternative `chart`
 *     * `chart` matches the characters `chart` literally (case insensitive)
 *   * 2nd Alternative `scale`
 *     * `scale` matches the characters `scale` literally (case insensitive)
 *   * 3rd Alternative `spark`
 *     * `spark` matches the characters `spark` literally (case insensitive)
 *
 * 2nd Alternative `\/users?\/?(login|googleFederatedLogin|register(Level)?)`
 * * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 * * `user` matches the characters `user` literally (case insensitive)
 * * `s` matches the character `s` with index 11510 (7316 or 1638) literally (case insensitive)
 * * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 * * `?` matches the previous token between `zero` and `one` times, as many times as possible, giving back as needed (greedy)
 *   * 3rd Capturing Group `(login|googleFederatedLogin|register(Level)?)`
 *     * 1st Alternative `login`
 *       * `login` matches the characters login literally (case insensitive)
 *     * 2nd Alternative `googleFederatedLogin`
 *       * `googleFederatedLogin` matches the characters `googleFederatedLogin` literally (case insensitive)
 *     * 3rd Alternative `register(Level)?`
 *       * `register` matches the characters `register` literally (case insensitive)
 *   * 4th Capturing Group `(Level)?`
 *     * `?` matches the previous token between `zero` and `one` times, as many times as possible, giving back as needed (greedy)
 *     * `Level` matches the characters `Level` literally (case insensitive)
 *
 * 3rd Alternative `(\/(documents|lucid(chart|scale|spark))\/editNewOrRegister)`
 *   * 5th Capturing Group `(\/(documents|lucid(chart|scale|spark))\/editNewOrRegister)`
 *     * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 *   * 6th Capturing Group `(documents|lucid(chart|scale|spark))`
 *     * 1st Alternative `documents`
 *       * `documents` matches the characters `documents` literally (case insensitive)
 *     * 2nd Alternative `lucid(chart|scale|spark)`
 *       * `lucid` matches the characters `lucid` literally (case insensitive)
 *   * 7th Capturing Group `(chart|scale|spark)`
 *     * 1st Alternative `chart`
 *       * `chart` matches the characters `chart` literally (case insensitive)
 *     * 2nd Alternative `scale`
 *       * `scale` matches the characters `scale` literally (case insensitive)
 *     * 3rd Alternative `spark`
 *       * `spark` matches the characters `spark` literally (case insensitive)
 * * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 * * `editNewOrRegister` matches the characters `editNewOrRegister` literally (case insensitive)
 */
export const isLucidAppAllowedPathnames =
  /(\/pricing\/lucid(chart|scale|spark))|\/users?\/?(login|googleFederatedLogin|register(Level)?)|(\/(documents|lucid(chart|scale|spark))\/editNewOrRegister)/gim;

/**
 * `^` asserts position at start of a line
 *
 * `http` matches the characters `http` literally (case insensitive)
 *
 * `s` matches the character `s` with index 11510 (7316 or 1638) literally (case insensitive)
 * * `?` matches the previous token between `zero` and `one` times, as many times as possible, giving back as needed (greedy)
 *
 * `:` matches the character `:` with index 5810 (3A16 or 728) literally (case insensitive)
 *
 * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 *
 * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 *
 * 1st Capturing Group `(.*)`
 * * `.` matches `any` character (except for line terminators)
 * * `*` matches the previous token between `zero` and `unlimited` times, as many times as possible, giving back as needed (greedy)
 *
 * 2nd Capturing Group `(www\.)?`
 * * `?` matches the previous token between `zero` and `one` times, as many times as possible, giving back as needed (greedy)
 * * `www` matches the characters `www` literally (case insensitive)
 * * `\.` matches the character `.` with index 4610 (2E16 or 568) literally (case insensitive)
 * * `lucid` matches the characters `lucid` literally (case insensitive)
 *
 * 3rd Capturing Group `(chart|scale|spark)`
 * * 1st Alternative `chart`
 *   * `chart` matches the characters `chart` literally (case insensitive)
 *
 * * 2nd Alternative `scale`
 *   * `scale` matches the characters `scale` literally (case insensitive)
 *
 * * 3rd Alternative `spark`
 *   * `spark` matches the characters `spark` literally (case insensitive)
 *
 * `.` matches any character (except for line terminators)
 *
 * `com` matches the characters `com` literally (case insensitive)
 */
export const isLucidMarketingDomain = /^https?:\/\/(.*)(www\.)?lucid(chart|scale|spark).com/gi;

/**
 * `^` asserts position at start of a line
 *
 * Match a single character present in the list below `[+]`
 * * `?` matches the previous token between `zero` and `one` times, as many times as possible, giving back as needed (greedy)
 * * `+` matches the character `+` with index 4310 (2B16 or 538) literally (case sensitive)
 *
 * Match a single character present in the list below `[(]`
 * * `?` matches the previous token between `zero` and `one` times, as many times as possible, giving back as needed (greedy)
 * * `(` matches the character `(` with index 4010 (2816 or 508) literally (case sensitive)
 *
 * Match a single character present in the list below `[0-9]`
 * * `{3}` matches the previous token exactly `3` times
 * * `0-9` matches a single character in the range between `0` (index 48) and `9` (index 57) (case sensitive)
 *
 * Match a single character present in the list below `[)]`
 * * `?` matches the previous token between `zero` and `one` times, as many times as possible, giving back as needed (greedy)
 * * `)` matches the character `)` with index 4110 (2916 or 518) literally (case sensitive)
 *
 * Match a single character present in the list below `[\-\s.]`
 * * `?` matches the previous token between `zero` and `one` times, as many times as possible, giving back as needed (greedy)
 * * `\-` matches the character `-` with index 4510 (2D16 or 558) literally (case sensitive)
 * * `\s` matches any whitespace character (equivalent to `[\r\n\t\f\v \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]`)
 * * `.` matches the character `.` with index 4610 (2E16 or 568) literally (case sensitive)
 *
 * Match a single character present in the list below `[0-9]`
 * * `{3}` matches the previous token exactly `3` times
 * * `0-9` matches a single character in the range between `0` (index 48) and `9` (index 57) (case sensitive)
 *
 * Match a single character present in the list below `[\-\s.]`
 * * `?` matches the previous token between `zero` and `one` times, as many times as possible, giving back as needed (greedy)
 * * `\-` matches the character `-` with index 4510 (2D16 or 558) literally (case sensitive)
 * * `\s` matches any whitespace character (equivalent to `[\r\n\t\f\v \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff]`)
 * * `.` matches the character `.` with index 4610 (2E16 or 568) literally (case sensitive)
 *
 * Match a single character present in the list below `[0-9]`
 * * `{4,6}` matches the previous token between `4` and `6` times, as many times as possible, giving back as needed (greedy)
 * * `0-9` matches a single character in the range between `0` (index 48) and `9` (index 57) (case sensitive)
 *
 * `$` asserts position at the end of a line
 */
export const isPhoneNumber = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/gim;

/**
 * ^ asserts position at start of a line
 * 1st Capturing Group `(\+?\d{1,2}\s?-?\.?)?`
 * `?` matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
 * `\+` matches the character `+` with index 4310 (2B16 or 538) literally (case insensitive)
 * `?` matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
 * `\d` matches a digit (equivalent to [0-9])
 * `{1,2}` matches the previous token between 1 and 2 times, as many times as possible, giving back as needed (greedy)
 * `\s` matches any whitespace character (equivalent to [\r\n\t\f\v \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff])
 * `?` matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
 * `-` matches the character `-` with index 4510 (2D16 or 558) literally (case insensitive)
 * \. matches the character `.` with index 4610 (2E16 or 568) literally (case insensitive)
 * `?` matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
 * `\(` matches the character `(` with index 4010 (2816 or 508) literally (case insensitive)
 * `\d` matches a digit (equivalent to [0-9])
 * {3} matches the previous token exactly 3 times
 * `\)` matches the character `)` with index 4110 (2916 or 518) literally (case insensitive)
 * `?` matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
 *
 * Match a single character present in the list below [\s.-]
 * `?` matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
 * `\s` matches any whitespace character (equivalent to [\r\n\t\f\v \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff])
 * `.` matches the character `.` with index 4610 (2E16 or 568) literally (case insensitive)
 * `-` matches the character `-` with index 4510 (2D16 or 558) literally (case insensitive)
 * This hyphen is treated literally, which might be confusing for others. Consider escaping it or placing at the start or end of the class.
 * `\d` matches a digit (equivalent to [0-9])
 * {3} matches the previous token exactly 3 times
 *
 * Match a single character present in the list below [\s.-]
 * `?` matches the previous token between zero and one times, as many times as possible, giving back as needed (greedy)
 * `\s` matches any whitespace character (equivalent to [\r\n\t\f\v \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff])
 * `.` matches the character `.` with index 4610 (2E16 or 568) literally (case insensitive)
 * `-` matches the character `-` with index 4510 (2D16 or 558) literally (case insensitive)
 * This hyphen is treated literally, which might be confusing for others. Consider escaping it or placing at the start or end of the class.
 * `\d` matches a digit (equivalent to [0-9])
 * {4} matches the previous token exactly 4 times
 * `$` asserts position at the end of a line
 */
export const isPhoneNumberNew = /^(\+?\d{1,2}\s?-?.?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/gim;

/**
 * 1st Capturing Group `(.*)`
 * * `.` matches any character (except for line terminators)
 * * `*` matches the previous token between zero and unlimited times, as many times as possible, giving back as needed (greedy)
 *
 * Non-capturing group `(?:<span\sclass=(?:'|"|\\")mktoErrorDetail(?:'|"|\\")>)`
 * `<span` matches the characters `<span` literally (case insensitive)
 * `\s` matches any whitespace character (equivalent to [\r\n\t\f\v \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff])
 * `class=` matches the characters `class=` literally (case insensitive)
 *
 * Non-capturing group `(?:'|"|\\")`
 * * 1st Alternative `'`
 * * *  `'` matches the character `'` with index 3910 (2716 or 478) literally (case insensitive)
 * * 2nd Alternative `"`
 * * * `"` matches the character `"` with index 3410 (2216 or 428) literally (case insensitive)
 * * 3rd Alternative `\\"`
 * * `\\` matches the character `\` with index 9210 (5C16 or 1348) literally (case insensitive)
 * `"` matches the character `"` with index 3410 (2216 or 428) literally (case insensitive)
 *
 * `mktoErrorDetail` matches the characters `mktoErrorDetail` literally (case insensitive)
 *
 * Non-capturing group `(?:'|"|\\")`
 * * 1st Alternative `'`
 * `'` matches the character `'` with index 3910 (2716 or 478) literally (case insensitive)
 * 2nd Alternative `"`
 * * `"` matches the character `"` with index 3410 (2216 or 428) literally (case insensitive)
 * 3rd Alternative `\\"`
 * * `\\` matches the character `\` with index 9210 (5C16 or 1348) literally (case insensitive)
 * * `"` matches the character `"` with index 3410 (2216 or 428) literally (case insensitive)
 * * `>` matches the character > with index 6210 (3E16 or 768) literally (case insensitive)
 *
 * 2nd Capturing Group `(.*)`
 * * `.` matches any character (except for line terminators)
 * * `*` matches the previous token between zero and unlimited times, as many times as possible, giving back as needed (greedy)
 *
 * Non-capturing group `(?:<\/span>)`
 * `<` matches the character `<` with index 6010 (3C16 or 748) literally (case insensitive)
 * `\/` matches the character `/` with index 4710 (2F16 or 578) literally (case insensitive)
 * `span>` matches the characters `span>` literally (case insensitive)
 */
export const filterMarketoError = /(.*)(?:<span\sclass=(?:'|"|\\")mktoErrorDetail(?:'|"|\\")>)(.*)(?:<\/span>)/gim;

/**
 * Match a single character present in the list below [()\s-]
 * `(` matches the character `(` with index 4010 (2816 or 508) literally (case sensitive)
 * `)` matches the character `)` with index 4110 (2916 or 518) literally (case sensitive)
 * `\s` matches any whitespace character (equivalent to [\r\n\t\f\v \u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000\ufeff])
 * `-` matches the character `-` with index 4510 (2D16 or 558) literally (case insensitive)
 */
export const isPhoneSeparatorCharacter = /[()\s-]/g;
